// pkg185 — opt-out eligibility at creation (D-185c): the required team picker becomes an optional "Who can bill" panel — everyone checked by default, uncheck to exclude (excluded_member_ids), ★ picks the default biller (default_biller_id); the "at least one team member" gate is gone — delivered 2026-10-30
// pkg72 — LawMighty retheme: brand hex sweep (#1a1a1a→#0F1838 NAVY_DEEP, #0f0f0f→#0A1028, #f8f8f8→#F5F1E8 CREAM, #c0392b→#B85042 TERRA) — delivered 2026-07-12
// pkg66 — success-card Portal URL derived from window.location.hostname (was hardcoded 'portal.ryan.law', wrong for every wildcard firm; the welcome EMAIL was already per-firm via pkg64) — delivered 2026-07-12
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { adminApi } from '../utils/adminApi';

const MOBILE_STYLES = `
.create-client-page .team-picker {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 8px;
  margin-bottom: 4px;
}
.create-client-page .team-option {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #fafafa;
  cursor: pointer;
  font-family: Arial, sans-serif;
  font-size: 13px;
  transition: background 0.1s, border-color 0.1s;
}
.create-client-page .team-option:hover { background: #f2f2f2; }
.create-client-page .team-option.selected {
  background: #e8f0fb;
  border-color: #2f5496;
}
.create-client-page .team-option input[type="checkbox"] {
  margin: 0;
  flex-shrink: 0;
}
.create-client-page .team-option .name {
  font-weight: 500;
  color: #0F1838;
}
.create-client-page .team-option .meta {
  color: #888;
  font-size: 11px;
  margin-top: 1px;
}
@media (max-width: 600px) {
  .create-client-page .form-row {
    flex-direction: column;
  }
  .create-client-page .form-row .form-group {
    flex: 1 1 auto !important;
    width: 100%;
    min-width: 0;
  }
  .create-client-page .team-picker {
    grid-template-columns: 1fr;
  }
}
`;

export default function AdminCreateClientPage() {
  // When this page is opened from the Contacts directory via "Convert to
  // Client", the contact's data arrives in query params. We seed the form
  // state from them on mount. `fromContact` is the contact's UUID; after a
  // successful create we POST back to link the contact to the new client
  // so the directory shows "→ Client".
  const [searchParams] = useSearchParams();
  const fromContact = searchParams.get('fromContact') || null;

  const [form, setForm] = useState({
    name:         searchParams.get('name')  || '',
    email:        searchParams.get('email') || '',
    phone:        searchParams.get('phone') || '',
    display_name: '', tempPassword: '',
    hourly_rate: '', case_type: '', practice_area: '', court: '', custom_case_type: '',
    fee_type: 'hourly',    // Wave 2: 'hourly' | 'flat_fee'
    flat_fee_amount: '',   // Only used when fee_type === 'flat_fee'
  });
  const [excludedIds, setExcludedIds] = useState(new Set()); // pkg185: unchecked = excluded
  const [defaultBillerId, setDefaultBillerId] = useState('');  // pkg185
  const [adminUsers, setAdminUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [caseTypes, setCaseTypes] = useState([]);
  const [practiceAreas, setPracticeAreas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  // Load admin users for the team picker
  useEffect(() => {
    adminApi.getAdminUsers()
      .then(users => setAdminUsers(users || []))
      .catch(err => setError(err.message))
      .finally(() => setLoadingUsers(false));
  }, []);

  // Load case types for the dropdown. Filter to active ones only.
  useEffect(() => {
    adminApi.listCaseTypes()
      .then(cts => setCaseTypes((cts || []).filter(ct => ct.is_active)))
      .catch(err => setError(err.message));
  }, []);

  // Load the firm's engaged practice areas (top of the assignment cascade).
  useEffect(() => {
    adminApi.getFirmPracticeAreas()
      .then(data => setPracticeAreas((data.practice_areas || []).filter(p => p.selected)))
      .catch(() => { /* non-fatal; cascade still works off case types */ });
  }, []);

  function toggleTeamMember(adminUserId) {
    setExcludedIds(prev => {
      const next = new Set(prev);
      if (next.has(adminUserId)) next.delete(adminUserId);
      else next.add(adminUserId);
      return next;
    });
    // Excluding the default biller clears the default (D-185b).
    setDefaultBillerId(d => (d === adminUserId ? '' : d));
  }

  // When the email field is filled, default display_name to the email address
  // if the display_name hasn't been set manually yet.
  function handleEmailBlur() {
    if (!form.display_name && form.email) {
      set('display_name', form.email.trim());
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (adminUsers.length > 0 && excludedIds.size >= adminUsers.length) {
      setError('At least one staff member must be able to bill on this client.');
      return;
    }
    setError(''); setResult(null); setLoading(true); setEmailSent(false);
    try {
      // If the user chose "Type a custom case type…", create it as a firm-owned
      // case type first (in the selected practice area), then assign its key —
      // so they don't have to leave this page to define a case type.
      let caseTypeKey = form.case_type || null;
      if (form.case_type === '__custom__') {
        const label = (form.custom_case_type || '').trim();
        if (!label) { setError('Enter a name for the custom case type.'); setLoading(false); return; }
        if (!form.practice_area) { setError('Pick a practice area for the custom case type.'); setLoading(false); return; }
        const ct = await adminApi.createFirmCaseType({
          label,
          practice_area: form.practice_area,
          court: form.court || undefined,
        });
        caseTypeKey = ct.key;
      }

      const data = await adminApi.createClient({
        ...form,
        hourly_rate: form.hourly_rate ? parseFloat(form.hourly_rate) : null,
        case_type:   caseTypeKey,
        practice_area: form.practice_area || null,
        court:       form.court || null,
        fee_type:    form.fee_type || 'hourly',
        flat_fee_amount: form.fee_type === 'flat_fee' && form.flat_fee_amount ?
          parseFloat(form.flat_fee_amount) : null,
        excluded_member_ids: [...excludedIds],           // pkg185
        default_biller_id: defaultBillerId || null,      // pkg185
      });
      setResult({ ...data.client, tempPassword: form.tempPassword });

      // If this client was created from a contact (via the Convert to
      // Client button on the contact detail page), link the contact to
      // the new client. Failure here is non-fatal — the client account
      // exists either way; the worst case is the contact directory still
      // shows it as un-converted.
      if (fromContact) {
        try {
          await adminApi.linkContactToClient(fromContact, data.client.id);
        } catch (linkErr) {
          console.warn('Could not link source contact to new client:', linkErr);
        }
      }

      setForm({
        name: '', email: '', phone: '',
        display_name: '', tempPassword: '',
        hourly_rate: '', case_type: '', practice_area: '', court: '', custom_case_type: '',
        fee_type: 'hourly', flat_fee_amount: '',
      });
      setTeamIds(new Set());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleSendEmail() {
    setSendingEmail(true);
    try {
      await adminApi.sendWelcome(result.id, result.tempPassword);
      setEmailSent(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSendingEmail(false);
    }
  }

  return (
    <div className="create-client-page">
      <style>{MOBILE_STYLES}</style>
      <h1 className="page-title">Create Client Account</h1>
      <p className="page-subtitle">
        You set the username and temporary password. Give these to the client directly — in person or by phone —
        rather than by email if there are any interception concerns. You can optionally send a welcome email after creating the account.
      </p>

      {error && <div className="alert alert-error">{error}</div>}

      {fromContact && !result && (
        <div className="alert alert-info" style={{ marginBottom: 16 }}>
          You&apos;re converting an imported contact into a client. The name, email, and phone
          below have been pre-filled — review them, set a username and temporary password, assign a
          team, and submit. The contact will be linked to the new client automatically.
        </div>
      )}

      {result && (
        <div className="card" style={{ background: '#f0faf5', borderColor: '#b7e3cf', marginBottom: 24 }}>
          <div style={{ fontSize: 15, marginBottom: 16, color: '#1e7e5e' }}>✓ Account created successfully</div>
          <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 13 }}>
            {[
              ['Client Name', result.name],
              ['Email', result.email],
              ['Display Name', result.display_name || result.email],
              ['Temporary Password', result.tempPassword],
              // pkg66: the admin panel and the client portal share a host, so the
              // page's own hostname IS the firm's portal URL — correct for every
              // wildcard firm (gatest.lawmighty.com) and for Hanzelik
              // (portal.ryan.law) with zero configuration. The welcome email's
              // URL is resolved server-side per-firm (pkg64) and was never wrong.
              ['Portal URL', window.location.hostname],
            ].map(([k, v]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #d0eedd' }}>
                <span style={{ color: '#555' }}>{k}</span>
                <strong style={{ color: '#0F1838' }}>{v}</strong>
              </div>
            ))}
          </div>
          <p style={{ marginTop: 14, fontSize: 12, color: '#555', fontFamily: 'Arial, sans-serif', marginBottom: 14 }}>
            The client must change their password on first login. Give them their credentials directly, or send the welcome email below if appropriate.
          </p>
          {!emailSent ? (
            <button className="btn btn-sm" onClick={handleSendEmail} disabled={sendingEmail}>
              {sendingEmail ? 'Sending...' : 'Send Welcome Email to Client'}
            </button>
          ) : (
            <div style={{ fontSize: 13, color: '#1e7e5e', fontFamily: 'Arial, sans-serif' }}>✓ Welcome email sent to {result.email}</div>
          )}
        </div>
      )}

      <div className="card">
        <form onSubmit={handleSubmit}>
          <div className="section-head" style={{ marginTop: 0 }}>Client Information</div>
          <div className="form-row">
            <div className="form-group">
              <label>Client Full Name</label>
              <input type="text" value={form.name} onChange={e => set('name', e.target.value)}
                placeholder="e.g. Jane Smith" required />
            </div>
            <div className="form-group">
              <label>Client Phone (optional)</label>
              <input type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="(423) 555-1234" />
            </div>
          </div>
          <div className="form-group">
            <label>Client Email Address</label>
            <input type="email" value={form.email}
              onChange={e => set('email', e.target.value)}
              onBlur={handleEmailBlur}
              placeholder="client@email.com" required />
          </div>
          <div className="form-group">
            <label>Practice Area</label>
            <select
              value={form.practice_area}
              onChange={e => { set('practice_area', e.target.value); set('case_type', ''); set('custom_case_type', ''); set('court', ''); }}
            >
              <option value="">— Select practice area (optional) —</option>
              {practiceAreas.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Case Type</label>
            <select
              value={form.case_type}
              disabled={!form.practice_area}
              onChange={e => {
                const v = e.target.value;
                set('case_type', v);
                if (v === '__custom__') { set('court', ''); return; }
                set('custom_case_type', '');
                const ct = caseTypes.find(c => c.key === v);
                set('court', ct?.court || '');
              }}
            >
              {!form.practice_area && <option value="">— Select a practice area first —</option>}
              {form.practice_area && <option value="">— Select case type (optional) —</option>}
              {form.practice_area && caseTypes
                .filter(ct => ct.practice_area === form.practice_area)
                .map(ct => <option key={ct.key} value={ct.key}>{ct.label}</option>)}
              {form.practice_area && <option value="__custom__">✏️ Type a custom case type…</option>}
            </select>
            {form.case_type === '__custom__' && (
              <input
                style={{ marginTop: 8 }}
                value={form.custom_case_type}
                onChange={e => set('custom_case_type', e.target.value)}
                placeholder="Name the case type (e.g. Guardianship) — we'll add it to this practice area"
              />
            )}
            {!form.practice_area && (
              <p style={{ fontSize: 12, fontFamily: 'Arial, sans-serif', color: '#888', marginTop: 4 }}>
                Choose a practice area above to see its case types.
              </p>
            )}
          </div>
          <div className="form-group">
            <label>Court (optional)</label>
            <input
              list="firm-courts"
              value={form.court}
              onChange={e => set('court', e.target.value)}
              placeholder="Type a court name (e.g. Circuit), or pick one you've used before"
            />
            <datalist id="firm-courts">
              {[...new Set(caseTypes.map(c => c.court).filter(Boolean))].map(c => <option key={c} value={c} />)}
            </datalist>
            <p style={{ fontSize: 12, fontFamily: 'Arial, sans-serif', color: '#888', marginTop: 4 }}>
              Type any court name to use it, or pick one you&apos;ve entered before. This only labels
              documents generated for this client. Leave blank if not applicable.
            </p>
          </div>

          <div className="section-head">Billing</div>
          <p style={{ fontSize: 13, fontFamily: 'Arial, sans-serif', color: '#555', marginBottom: 12 }}>
            Choose how this client will be billed. Hourly = time entries are billed at the attorney's rate.
            Flat fee = one set amount for the whole matter; time can still be logged for internal records
            (helpful for justifying a partial refund if you ever need to withdraw) but it does not add to the
            amount the client is charged.
          </p>
          <div className="form-row">
            <div className="form-group">
              <label>Fee Type</label>
              <select value={form.fee_type} onChange={e => set('fee_type', e.target.value)}>
                <option value="hourly">Hourly (bill by time)</option>
                <option value="flat_fee">Flat Fee (single set amount)</option>
              </select>
            </div>
            {form.fee_type === 'flat_fee' &&
            <div className="form-group">
              <label>Flat Fee Amount ($)</label>
              <input type="number" min="0" step="0.01" value={form.flat_fee_amount}
                onChange={e => set('flat_fee_amount', e.target.value)}
                placeholder="e.g. 2500.00" />
            </div>
            }
          </div>
          {form.fee_type === 'flat_fee' &&
          <p style={{ fontSize: 12, fontFamily: 'Arial, sans-serif', color: '#888', marginBottom: 16, marginTop: -4 }}>
            The flat fee counts as billed immediately when you create the client. You can edit this later
            from the client's Billing tab.
          </p>
          }

          <div className="section-head">Login Credentials</div>
          <p style={{ fontSize: 13, fontFamily: 'Arial, sans-serif', color: '#555', marginBottom: 16 }}>
            The client logs in with their email address. Set a temporary password and deliver it securely — in person or by phone.
          </p>
          <div className="form-row">
            <div className="form-group">
              <label>Display Name <span style={{ fontWeight: 400, color: '#888', fontSize: 12 }}>(optional — defaults to email)</span></label>
              <input type="text" value={form.display_name} onChange={e => set('display_name', e.target.value)}
                placeholder="e.g. Jane Smith" />
            </div>
            <div className="form-group">
              <label>Temporary Password</label>
              <input type="text" value={form.tempPassword} onChange={e => set('tempPassword', e.target.value)}
                placeholder="e.g. Cedar4821!" required />
            </div>
          </div>
          <p style={{ fontSize: 12, color: '#888', fontFamily: 'Arial, sans-serif', marginBottom: 20 }}>
            The client will be required to change this password when they first log in.
          </p>

          <div className="section-head">
            Who Can Bill <span style={{ fontWeight: 400, color: '#888', fontSize: 12 }}>(everyone, unless you uncheck them)</span>
          </div>
          <p style={{ fontSize: 13, fontFamily: 'Arial, sans-serif', color: '#555', marginBottom: 12 }}>
            Everyone at the firm can log time on this client unless you uncheck them here. Click ★ to make someone the
            default biller (pre-selected when logging time). Per-person rate overrides live on the Time tab.
          </p>
          {loadingUsers ? (
            <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#888' }}>Loading staff list...</p>
          ) : adminUsers.length === 0 ? (
            <div className="alert alert-error" style={{ marginBottom: 16 }}>
              No staff members exist yet. Add staff in <strong>Staff Management</strong> before creating a client.
            </div>
          ) : (
            <div className="team-picker" style={{ marginBottom: 20 }}>
              {adminUsers.map(u => {
                const selected = !excludedIds.has(u.id);
                const isDefault = defaultBillerId === u.id;
                const rate = u.default_hourly_rate ? `$${parseFloat(u.default_hourly_rate).toFixed(2)}/hr` : 'no default rate';
                const role = u.role ? u.role.charAt(0).toUpperCase() + u.role.slice(1) : 'Staff';
                return (
                  <label key={u.id} className={`team-option${selected ? ' selected' : ''}`}>
                    <input type="checkbox" checked={selected} onChange={() => toggleTeamMember(u.id)} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="name">{u.name}{isDefault ? ' · default' : ''}</div>
                      <div className="meta">{role} · {rate}</div>
                    </div>
                    <button type="button" disabled={!selected}
                      onClick={(e) => { e.preventDefault(); setDefaultBillerId(isDefault ? '' : u.id); }}
                      title={isDefault ? 'Clear default biller' : 'Make default biller'}
                      style={{ background: 'none', border: 'none', cursor: selected ? 'pointer' : 'default', fontSize: 18, lineHeight: 1, padding: '0 4px', color: isDefault ? '#b7870a' : (selected ? '#ccc' : '#eee') }}>★</button>
                  </label>
                );
              })}
            </div>
          )}

          <button type="submit" className="btn btn-primary" disabled={loading || loadingUsers || adminUsers.length === 0}>
            {loading ? <><span className="spinner" />Creating account...</> : 'Create Account'}
          </button>
        </form>
      </div>
    </div>
  );
}
